import React, { useState, useMemo } from 'react';
import {
  PlusIcon,
  BuildingOffice2Icon,
  PencilSquareIcon,
  TrashIcon,
  UserIcon,
  FunnelIcon,
} from '@heroicons/react/24/outline';
import { useLanguage } from 'contexts/LanguageContext';
import { useOrganizations } from 'hooks/useOrganizations';
import { useUsers } from 'hooks/useUsers';
import SearchBar from 'components/common/SearchBar';
import LoadingScreen from 'components/common/LoadingScreen';
import CommonPageHeader from '../../components/common/CommonPageHeader';
import { Tooltip } from 'components/common/Tooltip';
import Modal from 'components/common/Modal';
import Pagination from 'components/common/Pagination';
import toast from 'react-hot-toast';
import { organizationService } from 'services/organizationService';
import { Organization } from 'types/organization';
import { OrganizationSettings } from 'components/settings/OrganizationSettings';
import { useAuth } from 'contexts/AuthContext';
import ConfirmDeleteModal from 'components/common/ConfirmDeleteModal';

const Organizations = () => {
  const { t } = useLanguage();
  const { user } = useAuth();
  const { organizations, loading, refetch } = useOrganizations();
  const { users, loading: usersLoading } = useUsers();
  const [searchTerm, setSearchTerm] = useState('');
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isEditSettingsModalOpen, setIsEditSettingsModalOpen] = useState(false);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [selectedOrganizationId, setSelectedOrganizationId] = useState<string | null>(null);
  const [selectedOwner, setSelectedOwner] = useState<string>('all');
  const [selectedOrganization, setSelectedOrganization] = useState<Organization | null>(null);
  const [newOrgName, setNewOrgName] = useState('');

  // Biztosítjuk, hogy a t() függvény string értéket adjon vissza
  const getTranslation = (key: string, fallback: string): string => {
    const translation = t(key);
    return typeof translation === 'string' ? translation : fallback;
  };

  // Új szervezet sablon
  const [newOrganization, setNewOrganization] = useState<Organization>({
    id: '',
    ownerId: user?.uid || '',
    name: '',
    taxNumber: '',
    registrationNumber: '',
    address: {
      street: '',
      city: '',
      postalCode: '',
      country: ''
    },
    contact: {
      name: '',
      email: '',
      phone: '',
      position: ''
    },
    users: [],
    isActive: true,
    createdAt: new Date(),
    updatedAt: new Date()
  });

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  // Get owner name function
  const getOwnerName = (ownerId: string) => {
    const owner = users.find(user => user.id === ownerId);
    return owner?.displayName || getTranslation('common.unknownUser', 'Ismeretlen felhasználó');
  };

  // Get unique owners for dropdown
  const uniqueOwners = useMemo(() => {
    const ownerIds = Array.from(new Set(organizations.map(org => org.ownerId)));
    return ownerIds.map(id => ({
      id,
      name: getOwnerName(id)
    })).sort((a, b) => a.name.localeCompare(b.name));
  }, [organizations, users]);

  // Filter organizations based on search term and selected owner
  const filteredOrganizations = useMemo(() => {
    return organizations.filter(org => {
      // Filter by search term
      const searchLower = searchTerm.toLowerCase();
      const orgName = org.name?.toLowerCase() || '';
      const orgEmail = org.contact?.email?.toLowerCase() || '';
      const orgTaxNumber = org.taxNumber?.toLowerCase() || '';
      const ownerName = getOwnerName(org.ownerId)?.toLowerCase() || '';

      const matchesSearch =
        orgName.includes(searchLower) ||
        orgEmail.includes(searchLower) ||
        orgTaxNumber.includes(searchLower) ||
        ownerName.includes(searchLower);

      // Filter by selected owner
      const matchesOwner = selectedOwner === 'all' || org.ownerId === selectedOwner;

      return matchesSearch && matchesOwner;
    });
  }, [organizations, searchTerm, selectedOwner, users]);

  // Pagination logic
  const totalPages = Math.ceil(filteredOrganizations.length / itemsPerPage);
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentOrganizations = filteredOrganizations.slice(indexOfFirstItem, indexOfLastItem);

  const handlePageChange = (pageNumber: number) => {
    setCurrentPage(pageNumber);
  };

  const handleOwnerChange = (ownerId: string) => {
    setSelectedOwner(ownerId);
    setCurrentPage(1); // Reset to first page when changing filter
  };

  const handleItemsPerPageChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setItemsPerPage(Number(e.target.value));
    setCurrentPage(1); // Reset to first page when changing items per page
  };

  const openEditSettingsModal = (organization: Organization) => {
    setSelectedOrganization(organization);
    setIsEditSettingsModalOpen(true);
    setIsEditing(false);
  };

  const closeEditSettingsModal = () => {
    setIsEditSettingsModalOpen(false);
    setSelectedOrganization(null);
  };

  const openCreateModal = () => {
    setNewOrgName('');
    setIsCreateModalOpen(true);
  };

  const closeCreateModal = () => {
    setIsCreateModalOpen(false);
    setNewOrgName('');
  };

  const handleCreateSimpleOrganization = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!newOrgName.trim()) {
      toast.error(getTranslation('organizations.messages.nameRequired', 'Szervezet neve kötelező'));
      return;
    }

    try {
      // Létrehozzuk az alap szervezet adatokat csak a névvel
      const simpleOrgData = {
        ownerId: user?.uid || '',
        name: newOrgName.trim(),
        taxNumber: '-',
        registrationNumber: '-',
        address: {
          street: '-',
          city: '-',
          postalCode: '-',
          country: '-'
        },
        contact: {
          name: '-',
          email: '-',
          phone: '-',
          position: '-'
        },
        users: [],
        isActive: true,
        createdAt: new Date(),
        updatedAt: new Date()
      };

      const createdOrg = await organizationService.createOrganization(simpleOrgData);

      if (createdOrg) {
        toast.success(getTranslation('organizations.messages.createdSuccess', 'Szervezet sikeresen létrehozva'));
        closeCreateModal();
        refetch(); // Frissítjük a szervezetek listáját
      } else {
        toast.error(getTranslation('organizations.messages.createdError', 'Hiba történt a szervezet létrehozásakor'));
      }
    } catch (error) {
      console.error('Error creating organization:', error);
      toast.error(getTranslation('organizations.messages.createdError', 'Hiba történt a szervezet létrehozásakor'));
    }
  };

  const handleUpdateOrganization = async (updatedOrg: Organization) => {
    try {
      // Ellenőrizzük, hogy a bejövő objektum rendelkezik-e alapvető adatokkal
      if (!updatedOrg || !updatedOrg.id) {
        throw new Error('Invalid organization data detected');
      }

      // Keressük meg az eredeti szervezetet az organizations listából
      const originalOrg = organizations.find(org => org.id === updatedOrg.id);

      if (!originalOrg) {
        throw new Error('Original organization not found');
      }

      // Hozzuk létre a teljes objektumot, megtartva az eredeti mezőket is
      const completeOrgData: Organization = {
        ...originalOrg,           // Eredeti adatok megtartása alapként
        ...updatedOrg,            // Frissített mezők hozzáadása
        users: originalOrg.users, // Explicit módon megőrizzük a felhasználói listát
        id: originalOrg.id,       // Biztosítjuk, hogy az id nem változik
        ownerId: originalOrg.ownerId, // Biztosítjuk, hogy a tulajdonos id nem változik
        isActive: originalOrg.isActive, // Megtartjuk az aktív státuszt
        createdAt: originalOrg.createdAt, // Megtartjuk a létrehozás dátumát
        updatedAt: new Date()     // Frissítjük a módosítás dátumát
      };

      // Frissítjük a szervezetet a komplett adatokkal
      await organizationService.updateOrganization(completeOrgData.id, completeOrgData);
      closeEditSettingsModal();
      refetch(); // Refresh the organizations list
    } catch (error) {
      console.error('Error updating organization:', error);
      toast.error(getTranslation('organizations.messages.updatedError', 'Hiba történt a szervezet frissítésekor'));
    }
  };

  const confirmDelete = (id: string) => {
    setSelectedOrganizationId(id);
    setIsDeleteModalOpen(true);
  };

  const handleDelete = async () => {
    if (!selectedOrganizationId) return;

    try {
      await organizationService.deleteOrganization(selectedOrganizationId);
      toast.success(getTranslation('organizations.messages.deletedSuccess', 'Szervezet sikeresen törölve'));
      setIsDeleteModalOpen(false);
      setSelectedOrganizationId(null);
      refetch(); // Refresh the organizations list
    } catch (error) {
      console.error('Error deleting organization:', error);
      toast.error(getTranslation('organizations.messages.deletedError', 'Hiba történt a szervezet törlésekor'));
    }
  };

  if (loading || usersLoading) {
    return <LoadingScreen />;
  }

  return (
    <div className="space-y-6 pb-4 sm:pb-4">
      <CommonPageHeader
        title={getTranslation('organizations.title', 'Szervezetek')}
        subtitle="Szervezetek kezelése és beállítások"
        icon={BuildingOffice2Icon}
        showLastUpdate={false}
      >
        <button
          onClick={openCreateModal}
          className="inline-flex items-center px-4 py-2 bg-white/20 backdrop-blur-sm text-white rounded-lg hover:bg-white/30 transition-colors"
        >
          <PlusIcon className="w-5 h-5 mr-2" />
          {getTranslation('organizations.new', 'Új szervezet')}
        </button>
      </CommonPageHeader>

      <div className="flex flex-col md:flex-row gap-3">
        <div className="w-full md:flex-1">
          <SearchBar
            value={searchTerm}
            onChange={setSearchTerm}
            placeholder={getTranslation('common.search', 'Keresés')}
          />
        </div>

        <div className="flex-shrink-0">
          <div className="relative">
            <select
              value={selectedOwner}
              onChange={(e) => handleOwnerChange(e.target.value)}
              className="block w-full bg-white border border-gray-300 text-gray-700 rounded-md py-2 pl-10 pr-4 appearance-none focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-primary-500"
            >
              <option value="all">{getTranslation('organizations.filters.allOwners', 'Összes tulajdonos')}</option>
              {uniqueOwners.map(owner => (
                <option key={owner.id} value={owner.id}>
                  {owner.name}
                </option>
              ))}
            </select>
            <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
              <FunnelIcon className="h-4 w-4 text-gray-500" />
            </div>
          </div>
        </div>
      </div>

      {/* Desktop view */}
      <div className="hidden md:block bg-white rounded-lg overflow-hidden shadow-sm border border-gray-200">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-600">
                  {getTranslation('organizations.fields.name', 'Név')}
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-600">
                  {getTranslation('organizations.fields.email', 'Email')}
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-600">
                  {getTranslation('organizations.fields.phone', 'Telefon')}
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-600">
                  {getTranslation('organizations.fields.owner', 'Tulajdonos')}
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-600">
                  {getTranslation('organizations.fields.taxNumber', 'Adószám')}
                </th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-600 w-24">
                  {getTranslation('common.actions', 'Műveletek')}
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {currentOrganizations.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-4 py-4 text-center text-gray-500">
                    {getTranslation('organizations.noResults', 'Nincs találat')}
                  </td>
                </tr>
              ) : (
                currentOrganizations.map(org => (
                  <tr key={org.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3">
                      <div className="flex items-center">
                        <BuildingOffice2Icon className="h-5 w-5 text-gray-500 mr-2" />
                        <span className="text-gray-800">{org.name}</span>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-gray-700">
                      {org.contact?.email || '-'}
                    </td>
                    <td className="px-4 py-3 text-gray-700">
                      {org.contact?.phone || '-'}
                    </td>
                    <td className="px-4 py-3 text-gray-700">
                      <div className="flex items-center">
                        <UserIcon className="h-4 w-4 text-gray-500 mr-1.5" />
                        {getOwnerName(org.ownerId)}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-gray-700">
                      {org.taxNumber || '-'}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex justify-end space-x-2">
                        <Tooltip content={getTranslation('common.edit', 'Szerkesztés')}>
                          <button
                            onClick={() => openEditSettingsModal(org)}
                            className="text-gray-500 hover:text-primary-600"
                          >
                            <PencilSquareIcon className="h-4 w-4" />
                          </button>
                        </Tooltip>
                        <Tooltip content={getTranslation('common.delete', 'Törlés')}>
                          <button
                            onClick={() => confirmDelete(org.id)}
                            className="text-gray-500 hover:text-red-600"
                          >
                            <TrashIcon className="h-4 w-4" />
                          </button>
                        </Tooltip>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Mobile view */}
      <div className="md:hidden space-y-4 mb-4">
        {currentOrganizations.length === 0 ? (
          <div className="bg-white p-4 rounded-lg text-center text-gray-500 border border-gray-200 shadow-sm">
            {getTranslation('organizations.noResults', 'Nincs találat')}
          </div>
        ) : (
          currentOrganizations.map(org => (
            <div key={org.id} className="bg-white rounded-lg p-4 shadow-sm border border-gray-200">
              <div className="flex justify-between items-start mb-3">
                <div className="flex items-center">
                  <BuildingOffice2Icon className="h-5 w-5 text-gray-500 mr-2" />
                  <span className="font-medium text-gray-800">{org.name}</span>
                </div>
              </div>

              <div className="space-y-2 text-sm">
                <div className="flex items-start">
                  <span className="text-gray-500 w-24">
                    {getTranslation('organizations.fields.owner', 'Tulajdonos')}:
                  </span>
                  <div className="flex items-center text-gray-700">
                    <UserIcon className="h-3.5 w-3.5 text-gray-500 mr-1" />
                    {getOwnerName(org.ownerId)}
                  </div>
                </div>

                {org.contact?.email && (
                  <div className="flex items-start">
                    <span className="text-gray-500 w-24">
                      {getTranslation('organizations.fields.email', 'Email')}:
                    </span>
                    <span className="text-gray-700">{org.contact.email}</span>
                  </div>
                )}

                {org.contact?.phone && (
                  <div className="flex items-start">
                    <span className="text-gray-500 w-24">
                      {getTranslation('organizations.fields.phone', 'Telefon')}:
                    </span>
                    <span className="text-gray-700">{org.contact.phone}</span>
                  </div>
                )}

                {org.taxNumber && (
                  <div className="flex items-start">
                    <span className="text-gray-500 w-24">
                      {getTranslation('organizations.fields.taxNumber', 'Adószám')}:
                    </span>
                    <span className="text-gray-700">{org.taxNumber}</span>
                  </div>
                )}
              </div>

              <div className="flex justify-end space-x-3 mt-4 pt-2 border-t border-gray-200">
                <button
                  onClick={() => openEditSettingsModal(org)}
                  className="text-gray-500 hover:text-primary-600 p-1"
                >
                  <PencilSquareIcon className="h-4 w-4" />
                </button>
                <button
                  onClick={() => confirmDelete(org.id)}
                  className="text-gray-500 hover:text-red-600 p-1"
                >
                  <TrashIcon className="h-4 w-4" />
                </button>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Pagination */}
      {filteredOrganizations.length > 0 && (
        <div className="relative z-10">
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            itemsPerPage={itemsPerPage}
            totalItems={filteredOrganizations.length}
            indexOfFirstItem={indexOfFirstItem}
            indexOfLastItem={indexOfLastItem}
            onPageChange={handlePageChange}
            onItemsPerPageChange={handleItemsPerPageChange}
          />
        </div>
      )}

      {/* Delete confirmation modal */}
      <ConfirmDeleteModal
        isOpen={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        onConfirm={handleDelete}
        title={getTranslation('organizations.messages.confirmTitle', 'Szervezet törlése')}
        deleteMessage={getTranslation('organizations.messages.deleteQuestion', 'Biztosan törölni szeretné ezt a szervezetet?')}
        deleteWarning={getTranslation('organizations.messages.deleteWarning', 'Ezt a műveletet nem lehet visszavonni!')}
      />

      {/* Edit organization settings modal */}
      <Modal
        isOpen={isEditSettingsModalOpen}
        onClose={closeEditSettingsModal}
        title={getTranslation('organizations.editSettings', 'Szervezet beállításai')}
        size="6xl"
      >
        <div className="p-6">
          {selectedOrganization && (
            <OrganizationSettings
              organization={selectedOrganization}
              isEditing={isEditing}
              onEditingChange={setIsEditing}
              onOrganizationUpdate={handleUpdateOrganization}
            />
          )}
          <div className="flex justify-end space-x-3 mt-6">
            <button
              onClick={closeEditSettingsModal}
              className="px-4 py-2 bg-gray-200 text-gray-800 rounded-md hover:bg-gray-300"
            >
              {getTranslation('common.cancel', 'Mégsem')}
            </button>
          </div>
        </div>
      </Modal>

      {/* Create organization modal - Simple version */}
      <Modal
        isOpen={isCreateModalOpen}
        onClose={closeCreateModal}
        title={getTranslation('organizations.new', 'Új szervezet')}
        size="6xl"
      >
        <div className="p-6">
          <form onSubmit={handleCreateSimpleOrganization}>
            <div className="mb-6">
              <label htmlFor="orgName" className="block text-sm font-medium text-gray-700 mb-2">
                {getTranslation('organizations.fields.name', 'Név')} <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                id="orgName"
                value={newOrgName}
                onChange={(e) => setNewOrgName(e.target.value)}
                className="block w-full bg-white border border-gray-300 rounded-lg py-2.5 px-4 text-gray-700 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                placeholder={getTranslation('organizations.fields.name', 'Név')}
                required
              />
              <p className="mt-2 text-sm text-gray-500">
                {getTranslation('organizations.createInfo', 'Adja meg a szervezet nevét. A többi adatot később is megadhatja.')}
              </p>
            </div>
            <div className="flex justify-end space-x-3">
              <button
                type="button"
                onClick={closeCreateModal}
                className="px-4 py-2 bg-gray-200 text-gray-800 rounded-md hover:bg-gray-300"
              >
                {getTranslation('common.cancel', 'Mégsem')}
              </button>
              <button
                type="submit"
                className="px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700"
              >
                {getTranslation('common.create', 'Létrehozás')}
              </button>
            </div>
          </form>
        </div>
      </Modal>
    </div>
  );
};

export default Organizations;

